import type { TranslationBundle } from '@bms/common';

/**
 * The `activity` namespace: the desk's Activity page (audit P4, UX-13) — the
 * filter chips, day separators, the actor column and one sentence per event
 * type the feed shows (`components/activity/activityModel.ts`). Keys are read
 * as `activity.<key>`. Alarm names come from `grms.desk.alarm.<type>`.
 */
export const activity: TranslationBundle = {
  en: {
    title: 'Activity',
    live: 'Live',
    paused: 'Paused',
    filtersAria: 'Show activity of one kind',
    filter: {
      all: 'All', doors: 'Doors', keys: 'Keys', alarms: 'Alarms', housekeeping: 'Housekeeping', stays: 'Stays',
    },
    searchPlaceholder: 'Room or guest',
    searchAria: 'Filter activity by room or guest',
    today: 'Today',
    yesterday: 'Yesterday',
    loadMore: 'Show older',
    loading: 'Loading activity…',
    loadError: 'Activity could not be loaded.',
    retry: 'Try again',
    empty: 'Nothing has happened yet.',
    emptyFiltered: 'Nothing matches these filters.',
    clearFilters: 'Clear filters',
    openDoors: 'See door activity',
    unreadAria_one: '{{count}} new activity item',
    unreadAria_other: '{{count}} new activity items',
    column: { time: 'Time', room: 'Room', what: 'What happened', by: 'By' },
    actor: {
      device: 'device', lock: 'lock', contact: 'contact', edge: 'Edge', broker: 'broker', pms: 'PMS',
    },
    unknownAlarm: 'Alarm',
    denial: {
      card_expired: 'card expired',
      card_not_yet_valid: 'card not yet valid',
      card_revoked: 'card revoked',
      card_suspended: 'card suspended',
      card_unknown: 'unknown card',
      card_pending: 'card not written yet',
      not_in_schedule: 'outside its schedule',
      lock_schedule_closed: 'door closed by schedule',
      acl_denied: 'no access to this door',
      lock_no_actuator: 'door has no lock relay',
      wrong_site: 'card from another site',
      other: 'refused',
    },
    event: {
      access_granted: 'Access granted at {{lock}} · card {{card}}',
      access_granted_holder: 'Access granted at {{lock}} · {{holder}}',
      access_denied: 'Access denied at {{lock}} · card {{card}} · {{reason}}',
      access_granted_reader: 'Access granted at reader {{reader}} · card {{card}}',
      access_denied_reader: 'Access denied at reader {{reader}} · card {{card}} · {{reason}}',
      lock_left_open: 'Door {{lock}} left open',
      access_control: {
        lock: 'Relock sent to {{lock}}',
        unlock: 'Held unlock sent to {{lock}}',
        open: 'Open sent to {{lock}}',
        emergency_unlock: 'Emergency unlock of every door',
        other: 'Door {{lock}} configuration changed',
      },
      card_issued: 'Key issued · card {{card}}',
      card_issued_holder: 'Key issued · {{holder}}',
      card_revoked: 'Key revoked · card {{card}}',
      card_revoked_holder: 'Key revoked · {{holder}}',
      card_suspended: 'Key suspended · card {{card}}',
      card_reactivated: 'Key reactivated · card {{card}}',
      card_replaced: 'Key replaced · card {{card}}',
      card_retired: 'Key retired · card {{card}}',
      card_acl_updated: 'Key needs rewriting · card {{card}}',
      alarm_raised: '{{alarm}} · alarm raised',
      alarm_acknowledged: '{{alarm}} · alarm acknowledged',
      alarm_resolved: '{{alarm}} · alarm resolved',
      alarm_resolved_automatic: '{{alarm}} · resolved automatically',
      gateway_offline: 'Gateway {{gateway}} offline',
      gateway_online: 'Gateway {{gateway}} back online',
      cleaning_started: 'Cleaning started',
      cleaning_done: 'Cleaning done',
      inspected: 'Inspected · ready',
      mur_on: 'Make up room requested',
      mur_off: 'Make up room cleared',
      mur_acknowledged: 'Make up room acknowledged',
      dnd_on: 'Do not disturb on',
      dnd_off: 'Do not disturb off',
      maintenance: 'Service state changed',
      check_in: 'Checked in',
      check_in_guest: 'Checked in · {{guest}}',
      check_out: 'Checked out',
      stay_updated: 'Stay updated',
      room_move: 'Guest moved from {{from}} to {{to}}',
      note: 'Note · {{note}}',
      status_change: 'Room status changed',
    },
  },
  hr: {
    title: 'Aktivnost',
    live: 'Uživo',
    paused: 'Pauzirano',
    filtersAria: 'Prikaži aktivnost jedne vrste',
    filter: {
      all: 'Sve', doors: 'Vrata', keys: 'Ključevi', alarms: 'Alarmi', housekeeping: 'Domaćinstvo', stays: 'Boravci',
    },
    searchPlaceholder: 'Soba ili gost',
    searchAria: 'Filtriraj aktivnost po sobi ili gostu',
    today: 'Danas',
    yesterday: 'Jučer',
    loadMore: 'Prikaži starije',
    loading: 'Učitavanje aktivnosti…',
    loadError: 'Aktivnost se nije mogla učitati.',
    retry: 'Pokušaj ponovno',
    empty: 'Još se ništa nije dogodilo.',
    emptyFiltered: 'Ništa ne odgovara ovim filtrima.',
    clearFilters: 'Očisti filtre',
    openDoors: 'Prikaži aktivnost vrata',
    unreadAria_one: '{{count}} nova stavka aktivnosti',
    unreadAria_few: '{{count}} nove stavke aktivnosti',
    unreadAria_other: '{{count}} novih stavki aktivnosti',
    column: { time: 'Vrijeme', room: 'Soba', what: 'Što se dogodilo', by: 'Tko' },
    actor: {
      device: 'uređaj', lock: 'brava', contact: 'kontakt', edge: 'Edge', broker: 'broker', pms: 'PMS',
    },
    unknownAlarm: 'Alarm',
    denial: {
      card_expired: 'kartica je istekla',
      card_not_yet_valid: 'kartica još ne vrijedi',
      card_revoked: 'kartica je opozvana',
      card_suspended: 'kartica je obustavljena',
      card_unknown: 'nepoznata kartica',
      card_pending: 'kartica još nije zapisana',
      not_in_schedule: 'izvan rasporeda',
      lock_schedule_closed: 'vrata zatvorena rasporedom',
      acl_denied: 'nema pristupa ovim vratima',
      lock_no_actuator: 'vrata nemaju relej brave',
      wrong_site: 'kartica druge lokacije',
      other: 'odbijeno',
    },
    event: {
      access_granted: 'Pristup odobren na {{lock}} · kartica {{card}}',
      access_granted_holder: 'Pristup odobren na {{lock}} · {{holder}}',
      access_denied: 'Pristup odbijen na {{lock}} · kartica {{card}} · {{reason}}',
      access_granted_reader: 'Pristup odobren na čitaču {{reader}} · kartica {{card}}',
      access_denied_reader: 'Pristup odbijen na čitaču {{reader}} · kartica {{card}} · {{reason}}',
      lock_left_open: 'Vrata {{lock}} ostavljena otvorena',
      access_control: {
        lock: 'Zaključavanje poslano na {{lock}}',
        unlock: 'Trajno otključavanje poslano na {{lock}}',
        open: 'Otvaranje poslano na {{lock}}',
        emergency_unlock: 'Hitno otključavanje svih vrata',
        other: 'Promijenjena konfiguracija vrata {{lock}}',
      },
      card_issued: 'Ključ izdan · kartica {{card}}',
      card_issued_holder: 'Ključ izdan · {{holder}}',
      card_revoked: 'Ključ opozvan · kartica {{card}}',
      card_revoked_holder: 'Ključ opozvan · {{holder}}',
      card_suspended: 'Ključ obustavljen · kartica {{card}}',
      card_reactivated: 'Ključ ponovno aktiviran · kartica {{card}}',
      card_replaced: 'Ključ zamijenjen · kartica {{card}}',
      card_retired: 'Ključ povučen · kartica {{card}}',
      card_acl_updated: 'Ključ treba ponovno zapisati · kartica {{card}}',
      alarm_raised: '{{alarm}} · alarm podignut',
      alarm_acknowledged: '{{alarm}} · alarm potvrđen',
      alarm_resolved: '{{alarm}} · alarm riješen',
      alarm_resolved_automatic: '{{alarm}} · riješeno automatski',
      gateway_offline: 'Pristupnik {{gateway}} nije dostupan',
      gateway_online: 'Pristupnik {{gateway}} ponovno dostupan',
      cleaning_started: 'Čišćenje započeto',
      cleaning_done: 'Čišćenje završeno',
      inspected: 'Pregledano · spremno',
      mur_on: 'Zatraženo pospremanje sobe',
      mur_off: 'Zahtjev za pospremanje uklonjen',
      mur_acknowledged: 'Pospremanje sobe potvrđeno',
      dnd_on: 'Ne uznemiravaj uključeno',
      dnd_off: 'Ne uznemiravaj isključeno',
      maintenance: 'Promijenjeno stanje usluge',
      check_in: 'Prijava gosta',
      check_in_guest: 'Prijava gosta · {{guest}}',
      check_out: 'Odjava gosta',
      stay_updated: 'Boravak ažuriran',
      room_move: 'Gost premješten iz {{from}} u {{to}}',
      note: 'Bilješka · {{note}}',
      status_change: 'Promijenjen status sobe',
    },
  },
};
